/**
 * Account deletion request flow (status fetch + confirm + API + sign-out).
 *
 * Confirmation is handled INLINE inside the deletion sheet (no secondary dialog).
 * On iOS the deletion sheet is itself a stacked native Modal (rendered inside the
 * menu Modal), and presenting a further Modal — the global confirm Dialog — from
 * the already-busy root view controller fails silently, leaving the user stuck.
 * Keeping confirmation + errors inline avoids presenting any additional Modal.
 */

import { useCallback, useEffect, useState } from 'react'
import { useRouter } from 'expo-router'
import { Platform } from 'react-native'
import { useUserProfile } from '../../shared/contexts/UserProfileContext'
import { useApiClient } from '../../../helpers/apiClient.helper'
import { API_ENDPOINTS } from '../../../constants'
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext'
import { useAnalytics } from '../../analytics/useAnalytics'
import { ANALYTICS_EVENTS } from '../../analytics/events'
import {
  PRIVACY_AND_DATA_SCREEN,
  SIGN_IN_BANNER_ACCOUNT_DELETION_STARTED,
} from '../privacyAndData.constants'
import { setPendingSignInBannerMessage } from '../../signIn/helpers/signInBannerTransfer.helper'
import { logger } from '../../helpers/logger.helper'

const LOG_PREFIX = '[privacy-account-deletion]'

export function usePrivacyAccountDeletionFlow() {
  const router = useRouter()
  const { profile, isLoading: profileLoading } = useUserProfile()
  const apiClient = useApiClient()
  const { isLoaded: authLoaded, signOut } = useSupabaseAuth()
  const { track } = useAnalytics()

  const username = profile?.username ?? null
  const [isDeleting, setIsDeleting] = useState(false)
  const [existingDeletionStatus, setExistingDeletionStatus] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!authLoaded || !profile) return
    let cancelled = false
    apiClient(API_ENDPOINTS.users.accountDeletionStatus, { validateStatus: () => true })
      .then((res: { data: unknown }) => {
        if (cancelled) return
        const body = res.data as { success?: boolean; data?: { status?: string } | null }
        const status = body?.data?.status ?? null
        if (status === 'PENDING' || status === 'PROCESSING') {
          setExistingDeletionStatus(status)
        }
      })
      .catch(() => {
        // Non-critical
      })
    return () => {
      cancelled = true
    }
  }, [authLoaded, profile, apiClient])

  const doSignOutAndRedirect = useCallback(
    async (withBanner: boolean) => {
      try {
        await signOut()
      } catch (signOutErr) {
        logger.error(`${LOG_PREFIX} signOut after deletion request failed`, {
          error: signOutErr instanceof Error ? signOutErr.message : String(signOutErr),
        })
      }
      if (withBanner) {
        setPendingSignInBannerMessage(SIGN_IN_BANNER_ACCOUNT_DELETION_STARTED)
      }
      // Hard redirect on web to avoid the React state-vs-navigation race where the
      // sign-in page renders while isSignedIn is still true and bounces back to /app/.
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.location.href = '/sign-in'
      } else {
        router.replace('/sign-in')
      }
    },
    [router, signOut]
  )

  /**
   * Initial "Delete account" press. Returns true when the inline type-to-confirm
   * step should be shown. When the username is missing (Postgres record likely
   * already gone) signs the user out immediately and returns false.
   */
  const startDeletion = useCallback(async (): Promise<boolean> => {
    setErrorMessage(null)
    if (!username || !username.trim()) {
      logger.warn(`${LOG_PREFIX} Username missing on deletion attempt — signing out`)
      await doSignOutAndRedirect(false)
      return false
    }
    track(ANALYTICS_EVENTS.ACCOUNT_DELETION_STARTED)
    return true
  }, [username, doSignOutAndRedirect, track])

  const cancelDeletion = useCallback(() => {
    setErrorMessage(null)
    track(ANALYTICS_EVENTS.ACCOUNT_DELETION_CANCELLED)
  }, [track])

  /**
   * Final confirm (the phrase is validated by the component before calling this).
   * Errors are surfaced via `errorMessage` (inline) — never a secondary dialog.
   */
  const confirmDeletion = useCallback(async () => {
    if (!username || !username.trim()) {
      await doSignOutAndRedirect(false)
      return
    }

    setErrorMessage(null)
    setIsDeleting(true)
    try {
      const response = await apiClient(API_ENDPOINTS.users.accountDeletionRequest, {
        method: 'POST',
        validateStatus: () => true,
      })
      const status = response.status
      const body = response.data as { success?: boolean; message?: string }

      // 410 — server confirmed the account is already deleted: sign out.
      if (status === 410) {
        track(ANALYTICS_EVENTS.ACCOUNT_DELETION_REQUESTED)
        await doSignOutAndRedirect(true)
        return
      }

      // 408 / 503 with "Request timeout" — the global timeout middleware fired before
      // the synchronous deletion handler could respond, but the deletion completed
      // server-side. Sign out so the user doesn't stay logged in.
      const isTimeoutResponse =
        status === 408 ||
        (status === 503 &&
          typeof body?.message === 'string' &&
          body.message.toLowerCase().includes('timeout'))
      if (isTimeoutResponse) {
        track(ANALYTICS_EVENTS.ACCOUNT_DELETION_REQUESTED)
        await doSignOutAndRedirect(true)
        return
      }

      if (status >= 400 || body?.success === false) {
        track(ANALYTICS_EVENTS.ACCOUNT_DELETION_FAILED, { error_type: `http_${status}` })
        setErrorMessage(body?.message ?? PRIVACY_AND_DATA_SCREEN.TRY_AGAIN_LATER)
        return
      }

      track(ANALYTICS_EVENTS.ACCOUNT_DELETION_REQUESTED)
      await doSignOutAndRedirect(true)
    } catch (err) {
      logger.error(`${LOG_PREFIX} Deletion request failed`, {
        error: err instanceof Error ? err.message : String(err),
      })
      track(ANALYTICS_EVENTS.ACCOUNT_DELETION_FAILED, { error_type: 'network' })
      setErrorMessage(PRIVACY_AND_DATA_SCREEN.CHECK_CONNECTION)
    } finally {
      setIsDeleting(false)
    }
  }, [apiClient, username, doSignOutAndRedirect, track])

  const deletionAlreadyQueued =
    existingDeletionStatus === 'PENDING' || existingDeletionStatus === 'PROCESSING'

  const isReady = authLoaded && !(profileLoading && !profile)

  return {
    username,
    isReady,
    deletionAlreadyQueued,
    isDeleting,
    errorMessage,
    startDeletion,
    cancelDeletion,
    confirmDeletion,
  }
}
